import { useEffect, useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import {
  Crown,
  Headphones,
  Package,
  User as UserIcon,
  ChevronsUpDown,
  ChevronUp,
  ChevronDown,
  Edit3,
  UserMinus,
  LogIn,
  Search,
  Loader2,
} from 'lucide-react';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import { cn } from '@/lib/utils';
import type { User } from '@/services/users.service';
import { formatDateLong } from '@/lib/format';
import { useTranslation } from 'react-i18next';

interface UserDataTableProps {
  users: User[];
  isLoading?: boolean;
  onEdit: (user: User) => void;
  onDeactivate: (userId: string) => void;
  isDeactivatePending?: boolean;
  statusFilter?: string;
  onStatusFilterChange?: (value: string) => void;
  onImpersonate?: (user: User) => void;
  currentUserId?: string;
}

type SortKey = 'name' | 'role' | 'status' | 'lastLogin' | 'createdAt';

type SortConfig = {
  key: SortKey;
  direction: 'asc' | 'desc';
};

const roleMeta: Record<User['role'], { labelKey?: string; label: string; icon: ReactNode; className: string }> = {
  admin: {
    labelKey: 'common:role.admin', label: 'Administrator',
    icon: <Crown className="h-4 w-4 text-amber-600" />,
    className: 'bg-amber-100 text-amber-800 border border-amber-200',
  },
  agent: {
    labelKey: 'common:role.agent', label: 'Support Agent',
    icon: <Headphones className="h-4 w-4 text-blue-600" />,
    className: 'bg-blue-100 text-blue-800 border border-blue-200',
  },
  customer: {
    labelKey: 'ticket:td.customerLabel', label: 'Kunde',
    icon: <UserIcon className="h-4 w-4 text-slate-600" />,
    className: 'bg-slate-100 text-slate-700 border border-slate-200',
  },
  warehouse: {
    labelKey: 'common:role.warehouse', label: 'Lager-Mitarbeiter',
    icon: <Package className="h-4 w-4 text-orange-600" />,
    className: 'bg-orange-100 text-orange-800 border border-orange-200',
  },
};

const statusMeta: Record<User['status'], { labelKey?: string; label: string; className: string; dot: string }> = {
  active: {
    labelKey: 'common:state.active', label: 'Aktiv',
    className: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
    dot: 'bg-emerald-500',
  },
  inactive: {
    labelKey: 'common:state.inactive', label: 'Inaktiv',
    className: 'bg-amber-100 text-amber-700 border border-amber-200',
    dot: 'bg-amber-500',
  },
  suspended: {
    labelKey: 'common:state.blocked', label: 'Gesperrt',
    className: 'bg-red-100 text-red-700 border border-red-200',
    dot: 'bg-red-500',
  },
};

const roleFilterOptions: Array<{ labelKey?: string; label: string; value: 'all' | User['role'] }> = [
  { labelKey: 'settings:tbl.allRoles', label: 'Alle Rollen', value: 'all' },
  { labelKey: 'common:role.admin', label: 'Administrator', value: 'admin' },
  { labelKey: 'common:role.agent', label: 'Support Agent', value: 'agent' },
  { labelKey: 'common:role.warehouse', label: 'Lager-Mitarbeiter', value: 'warehouse' },
  { labelKey: 'ticket:td.customerLabel', label: 'Kunde', value: 'customer' },
];

const statusFilterOptions: Array<{ labelKey?: string; label: string; value: 'all' | User['status'] }> = [
  { labelKey: 'common:state.allStatus', label: 'Alle Status', value: 'all' },
  { labelKey: 'common:state.active', label: 'Aktiv', value: 'active' },
  { labelKey: 'common:state.inactive', label: 'Inaktiv', value: 'inactive' },
  { labelKey: 'common:state.blocked', label: 'Gesperrt', value: 'suspended' },
];

const PAGE_SIZE_OPTIONS = [10, 20, 50];

const sortIcon = (active: boolean, direction: 'asc' | 'desc') => {
  if (!active) {
    return <ChevronsUpDown className="h-4 w-4 text-slate-400" />;
  }

  return direction === 'asc'
    ? <ChevronUp className="h-4 w-4 text-slate-600" />
    : <ChevronDown className="h-4 w-4 text-slate-600" />;
};

const formatDate = (value?: string | null) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return formatDateLong(date);
};

const formatLastLogin = (value: string | null) => {
  if (!value) return 'Noch nie';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Noch nie';
  const diffMs = Date.now() - date.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffHours < 24) return `Vor ${diffHours}h`;
  if (diffHours < 48) return 'Gestern';
  return formatDate(value);
};

export const UserDataTable = ({ users, isLoading, onEdit, onDeactivate, isDeactivatePending, statusFilter: externalStatusFilter, onStatusFilterChange, onImpersonate, currentUserId }: UserDataTableProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState<'all' | User['role']>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | User['status']>(
    (externalStatusFilter as 'all' | User['status']) || 'active'
  );
  const [pageSize, setPageSize] = useState<number>(PAGE_SIZE_OPTIONS[0]);
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: 'createdAt', direction: 'desc' });

  useEffect(() => {
    setPage(1);
  }, [search, roleFilter, statusFilter, pageSize]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();

    return users.filter((user) => {
      const matchesSearch = term
        ? [user.name, user.email, user.firstName, user.lastName]
            .filter(Boolean)
            .some((value) => value.toLowerCase().includes(term))
        : true;

      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      const matchesStatus = statusFilter === 'all' || user.status === statusFilter;

      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, search, roleFilter, statusFilter]);

  const sortedUsers = useMemo(() => {
    const sorted = [...filteredUsers];

    sorted.sort((a, b) => {
      const dir = sortConfig.direction === 'asc' ? 1 : -1;

      switch (sortConfig.key) {
        case 'name':
          return a.name.localeCompare(b.name) * dir;
        case 'role':
          return a.role.localeCompare(b.role) * dir;
        case 'status':
          return a.status.localeCompare(b.status) * dir;
        case 'lastLogin': {
          const aTime = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
          const bTime = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
          return (aTime - bTime) * dir;
        }
        case 'createdAt':
        default: {
          const aTime = new Date(a.createdAt).getTime();
          const bTime = new Date(b.createdAt).getTime();
          return (aTime - bTime) * dir;
        }
      }
    });

    return sorted;
  }, [filteredUsers, sortConfig]);

  const totalPages = Math.max(1, Math.ceil(sortedUsers.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * pageSize;
  const pageItems = sortedUsers.slice(startIndex, startIndex + pageSize);

  const handleSort = (key: SortKey) => {
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: key === 'name' ? 'asc' : 'desc' },
    );
  };

  const handlePrevious = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setPage((prev) => Math.max(1, prev - 1));
  };

  const handleNext = (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setPage((prev) => Math.min(totalPages, prev + 1));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12 text-slate-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        <span className="text-sm">{t('settings:tbl.loadingUsers', 'Benutzer werden geladen...')}</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative w-full sm:max-w-sm">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('settings:tbl.searchUser', 'Nach Name oder E-Mail suchen')}
            className="h-9 pl-9 border-slate-200 bg-white focus-visible:ring-slate-200"
          />
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        </div>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Select value={roleFilter} onValueChange={(value) => setRoleFilter(value as typeof roleFilter)}>
            <SelectTrigger className="h-9 w-full sm:w-[160px] border-slate-200 bg-white focus:ring-slate-200">
              <SelectValue placeholder={t('common:field.role', 'Rolle')} />
            </SelectTrigger>
            <SelectContent align="end">
              {roleFilterOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.labelKey ? t(option.labelKey, option.label) : option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={(value) => {
            setStatusFilter(value as typeof statusFilter);
            onStatusFilterChange?.(value);
          }}>
            <SelectTrigger className="h-9 w-full sm:w-[150px] border-slate-200 bg-white focus:ring-slate-200">
              <SelectValue placeholder={t('common:field.status', 'Status')} />
            </SelectTrigger>
            <SelectContent align="end">
              {statusFilterOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.labelKey ? t(option.labelKey, option.label) : option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <Table>
          <TableHeader className="bg-slate-50">
            <TableRow className="hover:bg-slate-50 border-b border-slate-100">
              <TableHead className="min-w-[220px] py-3.5">
                <Button
                  variant="ghost"
                  className="-ml-3 h-auto px-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700"
                  onClick={() => handleSort('name')}
                >
                  <span className="flex items-center gap-2">
                    {t('settings:tbl.user', 'Benutzer')}
                    {sortIcon(sortConfig.key === 'name', sortConfig.direction)}
                  </span>
                </Button>
              </TableHead>
              <TableHead className="min-w-[160px] py-3.5">
                <Button
                  variant="ghost"
                  className="-ml-3 h-auto px-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700"
                  onClick={() => handleSort('role')}
                >
                  <span className="flex items-center gap-2">
                    {t('common:field.role', 'Rolle')}
                    {sortIcon(sortConfig.key === 'role', sortConfig.direction)}
                  </span>
                </Button>
              </TableHead>
              <TableHead className="min-w-[160px] py-3.5">
                <Button
                  variant="ghost"
                  className="-ml-3 h-auto px-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700"
                  onClick={() => handleSort('status')}
                >
                  <span className="flex items-center gap-2">
                    {t('common:field.status', 'Status')}
                    {sortIcon(sortConfig.key === 'status', sortConfig.direction)}
                  </span>
                </Button>
              </TableHead>
              <TableHead className="py-3.5">
                <Button
                  variant="ghost"
                  className="-ml-3 h-auto px-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700"
                  onClick={() => handleSort('lastLogin')}
                >
                  <span className="flex items-center gap-2">
                    {t('settings:tbl.lastLogin', 'Letzte Anmeldung')}
                    {sortIcon(sortConfig.key === 'lastLogin', sortConfig.direction)}
                  </span>
                </Button>
              </TableHead>
              <TableHead className="py-3.5">
                <Button
                  variant="ghost"
                  className="-ml-3 h-auto px-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-700"
                  onClick={() => handleSort('createdAt')}
                >
                  <span className="flex items-center gap-2">
                    {t('settings:tbl.createdAt', 'Erstellt am')}
                    {sortIcon(sortConfig.key === 'createdAt', sortConfig.direction)}
                  </span>
                </Button>
              </TableHead>
              <TableHead className="w-[120px] text-right py-3.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                {t('common:field.actions', 'Aktionen')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {pageItems.map((user) => {
              const roleInfo = roleMeta[user.role];
              const statusInfo = statusMeta[user.status];

              return (
                <TableRow key={user.id} className="group hover:bg-slate-50/50 border-b border-slate-100 last:border-0 transition-colors">
                  <TableCell className="py-4">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-xs font-medium text-white shadow-sm ring-2 ring-white">
                          {user.firstName?.[0] ?? user.name[0]}
                          {user.lastName?.[0] ?? user.name.split(' ')[1]?.[0] ?? ''}
                        </div>
                        <span className={cn('absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-white', statusInfo.dot)} />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-slate-900">{user.name}</p>
                        <p className="text-xs text-slate-500">{user.email}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="py-4">
                    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium border shadow-sm', roleInfo.className)}>
                      {roleInfo.icon}
                      {roleInfo.labelKey ? t(roleInfo.labelKey, roleInfo.label) : roleInfo.label}
                    </span>
                  </TableCell>
                  <TableCell className="py-4">
                    <span className={cn('inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-medium border shadow-sm', statusInfo.className)}>
                      <span className={cn('h-1.5 w-1.5 rounded-full', statusInfo.dot)} />
                      {statusInfo.labelKey ? t(statusInfo.labelKey, statusInfo.label) : statusInfo.label}
                    </span>
                  </TableCell>
                  <TableCell className="py-4 text-sm text-slate-600">{formatLastLogin(user.lastLogin)}</TableCell>
                  <TableCell className="py-4 text-sm text-slate-600">{formatDate(user.createdAt)}</TableCell>
                  <TableCell className="text-right py-4">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-900 hover:bg-white border border-transparent hover:border-slate-200" onClick={() => onEdit(user)}>
                        <Edit3 className="h-3.5 w-3.5" />
                      </Button>
                      {onImpersonate && user.status === 'active' && user.id !== currentUserId && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-blue-600 hover:bg-blue-50"
                            title={t('common:x3.signInAsUser', 'Als Benutzer anmelden')}
                          >
                            <LogIn className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t('common:x1.startSimulation', 'Simulation starten')}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('common:x3.wantYouAs', 'Möchten Sie sich als')} {user.name} ({user.email}) anmelden?
                              Sie können die Simulation jederzeit über die Leiste oben beenden.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-lg">{t('common:action.cancel', 'Abbrechen')}</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => onImpersonate(user)}
                              className="bg-blue-600 hover:bg-blue-700 rounded-lg"
                            >
                              {t('common:x1.startSimulation', 'Simulation starten')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      )}
                      {user.status === 'active' && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-slate-400 hover:text-amber-600 hover:bg-amber-50"
                            disabled={isDeactivatePending}
                          >
                            <UserMinus className="h-3.5 w-3.5" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t('common:x3.deactivateUser', 'Benutzer deaktivieren')}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('common:x1.wantTo', 'Möchten Sie')} {user.name} ({user.email}) deaktivieren?
                              Alle Daten bleiben erhalten und der Benutzer kann jederzeit über die Statusänderung wieder aktiviert werden.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-lg">{t('common:action.cancel', 'Abbrechen')}</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => onDeactivate(user.id)}
                              className="bg-amber-600 hover:bg-amber-700 rounded-lg"
                              disabled={isDeactivatePending}
                            >
                              {t('common:x3.deactivate', 'Deaktivieren')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
            {pageItems.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <div className="h-12 w-12 rounded-full bg-slate-50 flex items-center justify-center mb-3">
                       <UserIcon className="h-6 w-6 text-slate-300" />
                    </div>
                    <p className="font-medium text-slate-900">{t('common:state.noUsers', 'Keine Benutzer gefunden')}</p>
                    <p className="text-sm mt-1">{t('common:x1.adjustSearchFilters', 'Passen Sie den Suchbegriff oder die Filter an.')}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {sortedUsers.length > 0 && (
        <div className="flex flex-col gap-4 border-t border-slate-200 pt-4 md:flex-row md:items-center md:justify-between">
          <div className="text-sm text-muted-foreground">
            {t('common:x1.showing2', 'Zeigt')}{' '}
            <span className="font-medium text-slate-900">
              {sortedUsers.length === 0 ? 0 : startIndex + 1}-{Math.min(startIndex + pageSize, sortedUsers.length)}
            </span>{' '}
            von{' '}
            <span className="font-medium text-slate-900">{sortedUsers.length}</span> {t('common:x3.usersDative', 'Benutzern')}
          </div>
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              {t('settings:tbl.perPage', 'Pro Seite')}
              <Select value={pageSize.toString()} onValueChange={(value) => setPageSize(Number(value))}>
                <SelectTrigger className="h-8 w-[110px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAGE_SIZE_OPTIONS.map((option) => (
                    <SelectItem key={option} value={option.toString()}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Pagination className="justify-end">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={handlePrevious}
                    className={cn(currentPage === 1 && 'pointer-events-none opacity-50')}
                  />
                </PaginationItem>
                <PaginationItem>
                  <span className="px-3 text-sm text-muted-foreground">
                    {t('common:field.page', 'Seite')} {currentPage} von {totalPages}
                  </span>
                </PaginationItem>
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={handleNext}
                    className={cn(currentPage === totalPages && 'pointer-events-none opacity-50')}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDataTable;
